import React, { useEffect, useState } from 'react';
import { useNode, useEditor } from '@craftjs/core';
import { UIEditorBlockWidth, WidthSelectControl } from './WidthSelectControl';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useContextSelector } from 'use-context-selector';
import { InputValuesContext } from '../../UIEditorTypes';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import styles from './UIEditorBlocks.module.scss';
import { useTranslation } from 'react-i18next';

export interface TextProps {
  size: UIEditorBlockWidth;
  text: string;
  // Compact section headers need tighter spacing than the default Text block.
  isCompact: boolean;
  compactMarginTop?: number;
  compactMarginBottom?: number;
}

export const Text = (props: Partial<TextProps>) => {
  const { connectors, actions } = useNode((state) => ({
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));

  const { connect, drag } = connectors!;
  const { setProp } = actions;

  const { size, text, isCompact, compactMarginTop, compactMarginBottom } = {
    ...TextDefaultProps,
    ...props,
  };

  const i18next = useScopedTranslation('editor.blocks');
  const i18n = useTranslation();

  const isOnlyView = useContextSelector(
    InputValuesContext,
    (v) => v.isOnlyView,
  );

  const dataBlockTypeName = useContextSelector(
    InputValuesContext,
    (v) => v.dataBlockTypeName,
  );

  const [value, setValue] = useState<string>(text);

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  useEffect(() => {
    setProp((props: TextProps) => (props.text = value));
  }, [value]);

  const editableText = (
    <Input
      placeholder={i18next.t('text_placeholder')}
      value={value}
      onChange={(e) => setValue(e.target?.value)}
    />
  );

  const notEditableText = (
    <div
      data-has-padding={!(isOnlyView || enabled)}
      className={styles.textContainer}
    >
      {i18n.t(
        'data-block-fields:' +
          dataBlockTypeName +
          '.text_block.' +
          value.toLowerCase(),
        value,
      )}
    </div>
  );

  return (
    <div
      className={styles.container}
      style={{
        width: size,
      }}
      ref={(ref) => connect(drag(ref!))}
    >
      <div
        className={styles.textBlockContainer}
        data-compact={isCompact}
        style={
          isCompact
            ? {
                marginTop: compactMarginTop,
                marginBottom: compactMarginBottom,
              }
            : undefined
        }
      >
        {enabled ? editableText : notEditableText}
      </div>
    </div>
  );
};

const TextSettings = () => {
  const { actions, props } = useNode((node) => ({
    props: node.data.props as TextProps,
  }));
  const { setProp } = actions;

  return (
    <div className={styles.settings}>
      <WidthSelectControl props={props} setProp={setProp} />
    </div>
  );
};

export const TextDefaultProps: TextProps = {
  text: 'Text',
  size: UIEditorBlockWidth.Quarter,
  isCompact: false,
};

Text.craft = {
  defaultProps: TextDefaultProps,
  related: {
    settings: TextSettings,
  },
};
